import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { CSSTransitionGroup } from 'react-transition-group';

import { isMainTaxon } from '../../utils/taxons';

import Loader from '../../components/loader/Loader';
import Masonry from '../../components/masonry/Masonry';
import ReviewsSelect from './ReviewsSelect';
import ReviewsBadge from './ReviewsBadge';
import ReviewItem from './ReviewItem';
import { fetchReviews } from '../../ducks/reviews';

import './ReviewsList.sass';

const PAGE_LIMIT = 24;

// Maps the sort dropdown to the endpoint's ratingOrder param. "Newest" omits it
// entirely (the API always tiebreaks/defaults to createdAt DESC).
const SORT_OPTIONS = [
  { key: 'newest', labelId: 'section.reviews.sort.newest', ratingOrder: undefined },
  { key: 'highest', labelId: 'section.reviews.sort.highest', ratingOrder: 'desc' },
  { key: 'lowest', labelId: 'section.reviews.sort.lowest', ratingOrder: 'asc' },
];

const ReviewsList = ({ dispatch, games, intl }) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState();
  const [taxonCode, setTaxonCode] = useState();
  const [sortKey, setSortKey] = useState('newest');

  const ratingOrder = SORT_OPTIONS.find((o) => o.key === sortKey).ratingOrder;

  const gameOptions = [
    { value: undefined, label: intl.formatMessage({ id: 'section.reviews.filter.all-games' }) },
    ...games.map((game) => ({ value: game.code, label: game.name })),
  ];

  const sortOptions = SORT_OPTIONS.map((option) => ({
    value: option.key,
    label: intl.formatMessage({ id: option.labelId }),
  }));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    dispatch(fetchReviews({ page, limit: PAGE_LIMIT, taxonCode, ratingOrder }))
      .then((res) => {
        if (cancelled) {
          return;
        }
        setReviews((res.content && res.content.items) || []);
        setPages((res.meta && res.meta.pages) || 1);
        setTotal(res.meta && res.meta.total);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, taxonCode, sortKey]);

  // Changing the filter/sort restarts pagination from the first page.
  const selectGame = (code) => {
    setTaxonCode(code);
    setPage(1);
  };

  const selectSort = (key) => {
    setSortKey(key);
    setPage(1);
  };

  const goTo = (next) => {
    if (next < 1 || next > pages || next === page) {
      return;
    }
    setPage(next);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Fragment>
      {loading && page === 1 ? (
        <Loader section />
      ) : (
        <ReviewsBadge reviewsCount={total} withExternalLinks />
      )}

      <div className="reviews-controls">
        {/* TODO: re-enable once taxon filtering is fixed on the backend.
        <ReviewsSelect
          wide
          options={gameOptions}
          value={taxonCode}
          onChange={selectGame}
        />
        */}

        <ReviewsSelect
          options={sortOptions}
          value={sortKey}
          onChange={selectSort}
        />
      </div>

      {!loading && !reviews.length ? (
        <p className="reviews-empty">
          <FormattedMessage id="section.reviews.empty" />
        </p>
      ) : (
        <CSSTransitionGroup
          key={`${taxonCode || 'all'}-${sortKey}`}
          component={Masonry}
          transitionName="masonry"
          transitionEnterTimeout={500}
          transitionLeaveTimeout={500}
        >
          {reviews.map((review, idx) => (
            <ReviewItem key={`${page}-${idx}`} {...review} />
          ))}
        </CSSTransitionGroup>
      )}

      {pages > 1 && (
        <div className="reviews-pagination">
          <button
            type="button"
            className="btn btn--filled reviews-pagination__btn"
            onClick={() => goTo(page - 1)}
            disabled={loading || page <= 1}
          >
            <FormattedMessage id="section.reviews.pagination.previous" />
          </button>

          <span className="reviews-pagination__status">
            <FormattedMessage
              id="section.reviews.pagination.page"
              values={{ page, pages }}
            />
          </span>

          <button
            type="button"
            className="btn btn--filled reviews-pagination__btn"
            onClick={() => goTo(page + 1)}
            disabled={loading || page >= pages}
          >
            <FormattedMessage id="section.reviews.pagination.next" />
          </button>
        </div>
      )}
    </Fragment>
  );
};

export default injectIntl(connect(({ taxons }) => ({
  games: (taxons.root.children || []).filter(isMainTaxon),
}))(ReviewsList));



// WEBPACK FOOTER //
// ./components/reviews/ReviewsList.js